import { useState } from "react";
import { toast } from "sonner";
import { Smartphone, CreditCard, Loader2, ShieldCheck, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { money, ghsOnly, isLocal, localAmount, getGeo } from "../lib/currency";
import { CountryPicker } from "./CurrencyGate";

const BRAND = {
  "MTN MoMo": ["#FFCC00", "#1a1a1a"], "Telecel Cash": ["#E11400", "#fff"], "AirtelTigo Money": ["#0033A0", "#fff"],
  "M-Pesa": ["#4CAF50", "#fff"], "Airtel Money": ["#E40000", "#fff"], "Tigo Pesa": ["#00377B", "#fff"],
  "Orange Money": ["#FF7900", "#fff"], Wave: ["#1DC4FF", "#06283D"], "Moov Money": ["#0067B1", "#fff"], "T-Money": ["#F7B500", "#1a1a1a"],
  OPay: ["#1DCF9F", "#06283D"],
};
const CARDLIKE = ["Card", "PayPal", "Bank Transfer", "Instant EFT"];
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function MomoDialog({ item, endpoint, extra = {}, open, onOpenChange, onPaid }) {
  const geo = getGeo();
  const methods = geo.methods.includes("Card") ? geo.methods : [...geo.methods, "Card"];
  const [provider, setProvider] = useState(methods[0]);
  const [phone, setPhone] = useState("");
  const [stage, setStage] = useState("form");
  const card = CARDLIKE.includes(provider);
  const stripe = provider === "Card";

  const payByCard = async () => {
    setStage("redirect");
    try {
      const { data } = await api.post("/payments/stripe/checkout", { item: extra.course_slug || "membership", country: geo.country_code, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setStage("form");
    }
  };

  const pay = async () => {
    setStage("prompt");
    await new Promise((r) => setTimeout(r, 1800));
    try {
      const { data } = await api.post(endpoint, { ...extra, provider, phone, country: geo.country_code, display_currency: geo.currency, display_amount: localAmount(item.price) });
      toast.success(`Payment received · Ref ${data.payment.reference}`);
      onPaid(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setStage("form");
    }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-lg" data-testid="momo-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Complete your payment</DialogTitle>
          <DialogDescription>{item.title} · <b className="text-[#5B2A6E]" data-testid="momo-amount">{money(item.price)}</b>{item.note ? ` · ${item.note}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 text-xs text-[#6b5a73]">
          <span>Paying from</span><CountryPicker testid="momo-country-picker" />
        </div>
        {isLocal() && <div className="text-[11px] text-[#8a7a92] -mt-2" data-testid="momo-base-price">Billed as {ghsOnly(item.price)} and converted at today's rate. Your provider may round slightly.</div>}
        {stripe ? (
          <div className="rounded-md bg-emerald-50 text-emerald-800 text-xs px-3 py-2 font-semibold flex items-center gap-2" data-testid="stripe-notice">
            <Lock className="w-3.5 h-3.5" />Secure card payment by Stripe - you'll enter your card on Stripe's page.
          </div>
        ) : (
          <div className="rounded-md bg-[#FFF4D4] text-[#8a6a00] text-xs px-3 py-2 font-semibold" data-testid="momo-mock-notice">
            SIMULATED PAYMENT - {provider} is not connected yet, no real money is charged. Choose Card to pay for real.
          </div>
        )}
        {stage === "redirect" ? (
          <div className="py-10 text-center" data-testid="stripe-redirecting">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#5B2A6E]" />
            <p className="mt-4 font-semibold">Taking you to secure checkout…</p>
          </div>
        ) : stage === "prompt" ? (
          <div className="py-10 text-center" data-testid="momo-awaiting">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#5B2A6E]" />
            <p className="mt-4 font-semibold">{card ? `Confirming your ${provider} payment` : `Approve the prompt on ${phone}`}</p>
            <p className="text-sm text-[#6b5a73]">{card ? "This only takes a moment" : `Enter your ${provider} PIN to confirm ${money(item.price)}`}</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {methods.map((name) => {
                const [bg, fg] = BRAND[name] || ["#F4EEF7", "#5B2A6E"];
                return (
                  <button key={name} onClick={() => setProvider(name)} data-testid={`momo-provider-${slugify(name)}`}
                    className={`rounded-md p-3 text-xs font-bold border-2 transition-transform ${provider === name ? "border-[#5B2A6E] scale-[1.03]" : "border-transparent"}`}
                    style={{ background: bg, color: fg }}>{name}</button>
                );
              })}
            </div>
            {stripe ? (
              <button className="dx-btn-gold w-full py-3" onClick={payByCard} data-testid="stripe-pay-btn">
                <CreditCard className="w-4 h-4" />Pay {money(item.price)} by card
              </button>
            ) : (<>
            <label className="block">
              <span className="dx-label">{card ? (provider === "Card" ? "Card number" : provider === "PayPal" ? "PayPal phone number" : "Account number") : `${provider} number`}</span>
              <div className="relative">
                {card ? <CreditCard className="w-4 h-4 absolute left-3 top-3 text-[#8a7a92]" /> : <Smartphone className="w-4 h-4 absolute left-3 top-3 text-[#8a7a92]" />}
                <input className="dx-input pl-9" inputMode="numeric" placeholder={card ? "1234 5678 9012 3456" : "Mobile number"} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="momo-phone-input" />
              </div>
            </label>
            <button className="dx-btn-gold w-full py-3" onClick={pay} disabled={phone.replace(/\D/g, "").length < 7} data-testid="momo-pay-btn">
              <ShieldCheck className="w-4 h-4" />Pay {money(item.price)}
            </button>
            </>)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
